import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  ApiResponse,
  PagedResult,
  TransferRequest,
  AddStockRequest,
  AddStockBatchRequest,
  WriteOffRequest,
  AdjustStockRequest,
  StockHistoryItem,
  InventoryAnalyticsDto,
  RevenueTrendItem,
  CategoryAnalyticsItem,
  HourlySalesPoint,
} from '../models/product.models';
import { ApiService } from './api.service';

export interface StockHistoryFilter {
  productId?: string;
  storeIds?: string[];
  type?: number;
  fromDate?: string;
  toDate?: string;
  page: number;
  pageSize: number;
}

@Injectable({ providedIn: 'root' })
export class InventoryService extends ApiService {
  private readonly endpoint = `${environment.apiUrl}/api/Inventory`;

  transfer(data: TransferRequest): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(`${this.endpoint}/transfer`, data);
  }

  addStock(data: AddStockRequest): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(`${this.endpoint}/add-stock`, data);
  }

  /**
   * Ko'p mahsulotli, PULSIZ kirim — "Mahsulot kirimi" dagi to'lov/qarz bloki
   * O'CHIRILGAN holatda shu chaqiriladi. Kassa/kreditorlik hisobotiga tegmaydi.
   */
  addStockBatch(data: AddStockBatchRequest): Observable<ApiResponse<{ productCount: number }>> {
    return this.http.post<ApiResponse<{ productCount: number }>>(
      `${this.endpoint}/add-stock-batch`,
      data,
    );
  }

  writeOff(data: WriteOffRequest): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(`${this.endpoint}/write-off`, data);
  }

  /**
   * Qoldiqni tuzatish — kirim/chiqim EMAS. Analitikaga (foyda, zarar, kirimlar)
   * ta'sir qilmaydi, faqat qoldiqni to'g'rilaydi va tarixda Adjustment bo'lib qoladi.
   */
  adjustStock(data: AdjustStockRequest): Observable<ApiResponse<{ delta: number }>> {
    return this.http.post<ApiResponse<{ delta: number }>>(`${this.endpoint}/adjust`, data);
  }

  getAnalytics(
    storeIds?: string[],
    fromDate?: string,
    toDate?: string,
    /** Bo'sh bo'lsa backend tenant kotirovka valyutasini oladi (I11). */
    currency?: string,
  ): Observable<ApiResponse<InventoryAnalyticsDto>> {
    let params = new HttpParams();
    if (storeIds?.length) storeIds.forEach((id) => (params = params.append('storeIds', id)));
    if (fromDate) params = params.set('fromDate', fromDate);
    if (toDate) params = params.set('toDate', toDate);
    if (currency) params = params.set('currency', currency);
    return this.http.get<ApiResponse<InventoryAnalyticsDto>>(`${this.endpoint}/analytics`, {
      params,
    });
  }

  getRevenueTrend(
    storeIds?: string[],
    fromDate?: string,
    toDate?: string,
    /** Bo'sh bo'lsa backend tenant kotirovka valyutasini oladi (I11). */
    currency?: string,
  ): Observable<ApiResponse<RevenueTrendItem[]>> {
    let params = new HttpParams();
    if (storeIds?.length) storeIds.forEach((id) => (params = params.append('storeIds', id)));
    if (fromDate) params = params.set('fromDate', fromDate);
    if (toDate) params = params.set('toDate', toDate);
    if (currency) params = params.set('currency', currency);
    return this.http.get<ApiResponse<RevenueTrendItem[]>>(
      `${this.endpoint}/analytics/revenue-trend`,
      { params },
    );
  }

  getCategoryAnalytics(
    storeIds?: string[],
    fromDate?: string,
    toDate?: string,
    /** Bo'sh bo'lsa backend tenant kotirovka valyutasini oladi (I11). */
    currency?: string,
  ): Observable<ApiResponse<CategoryAnalyticsItem[]>> {
    let params = new HttpParams();
    if (storeIds?.length) storeIds.forEach((id) => (params = params.append('storeIds', id)));
    if (fromDate) params = params.set('fromDate', fromDate);
    if (toDate) params = params.set('toDate', toDate);
    if (currency) params = params.set('currency', currency);
    return this.http.get<ApiResponse<CategoryAnalyticsItem[]>>(
      `${this.endpoint}/analytics/categories`,
      { params },
    );
  }

  getHourlySales(
    storeIds?: string[],
    fromDate?: string,
    toDate?: string,
    /** Bo'sh bo'lsa backend tenant kotirovka valyutasini oladi (I11). */
    currency?: string,
  ): Observable<ApiResponse<HourlySalesPoint[]>> {
    let params = new HttpParams();
    if (storeIds?.length) storeIds.forEach((id) => (params = params.append('storeIds', id)));
    if (fromDate) params = params.set('fromDate', fromDate);
    if (toDate) params = params.set('toDate', toDate);
    if (currency) params = params.set('currency', currency);
    return this.http.get<ApiResponse<HourlySalesPoint[]>>(`${this.endpoint}/analytics/hourly`, {
      params,
    });
  }

  getHistory(filter: StockHistoryFilter): Observable<ApiResponse<PagedResult<StockHistoryItem>>> {
    let params = new HttpParams().set('page', filter.page).set('pageSize', filter.pageSize);
    if (filter.productId) params = params.set('productId', filter.productId);
    if (filter.storeIds?.length)
      filter.storeIds.forEach((id) => (params = params.append('storeIds', id)));
    if (filter.type != null) params = params.set('type', filter.type);
    if (filter.fromDate) params = params.set('fromDate', filter.fromDate);
    if (filter.toDate) params = params.set('toDate', filter.toDate);
    return this.http.get<ApiResponse<PagedResult<StockHistoryItem>>>(`${this.endpoint}/history`, {
      params,
    });
  }
}
